import {
  AlertCircle,
  ShieldAlert,
  Info,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Users,
  Tag,
  Layers,
  GitBranch,
  Target,
  Zap,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function ImportanceBadge({ importance }) {
  var color = {
    low: 'bg-muted text-muted-foreground',
    medium: 'bg-blue-500/10 text-blue-400',
    high: 'bg-amber-500/10 text-amber-400',
    critical: 'bg-red-500/10 text-red-400',
  }[importance] || 'bg-muted text-muted-foreground';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
      {importance}
    </span>
  );
}

function InferredBadge() {
  return (
    <div className="mt-3 flex items-center gap-1 text-amber-400 text-xs">
      <span>⚠</span>
      <span className="font-medium">Inferred</span>
      <span className="text-muted-foreground">— not directly grounded in source</span>
    </div>
  );
}

function BlockShell({ children, grounded, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      {children}
      {grounded === false && <InferredBadge />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. section_heading
// ---------------------------------------------------------------------------
function SectionHeading({ block }) {
  var { level, text } = block;
  var classes = {
    1: 'text-3xl font-bold text-foreground mt-8 mb-4',
    2: 'text-2xl font-semibold text-foreground mt-6 mb-3',
    3: 'text-xl font-semibold text-muted-foreground mt-4 mb-2',
  }[level] || 'text-xl font-semibold mt-4 mb-2';
  return (
    <BlockShell grounded={block.grounded}>
      {level === 1 && <h1 className={classes}>{text}</h1>}
      {level === 2 && <h2 className={classes}>{text}</h2>}
      {level === 3 && <h3 className={classes}>{text}</h3>}
      {level === 2 && <div className="h-px bg-border mb-2" />}
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 2. narrative
// ---------------------------------------------------------------------------
function Narrative({ block }) {
  return (
    <BlockShell grounded={block.grounded} className="py-2">
      <p className="text-foreground leading-relaxed text-base">{block.text}</p>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 3. key_insight
// ---------------------------------------------------------------------------
function KeyInsight({ block }) {
  return (
    <BlockShell grounded={block.grounded}>
      <div className="border-l-4 border-primary pl-4 py-3 bg-card rounded-r-lg">
        <p className="font-bold text-foreground text-base mb-1">{block.headline}</p>
        <p className="text-muted-foreground text-sm leading-relaxed">{block.body}</p>
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 4. process_flow
// ---------------------------------------------------------------------------
function ProcessStep({ step }) {
  var expanded = React.useState(false);
  var isOpen = expanded[0];
  var setIsOpen = expanded[1];

  return (
    <div className="flex gap-4 group">
      {/* circle */}
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
          {step.order}
        </div>
        <div className="w-px flex-1 bg-border mt-1" />
      </div>
      {/* content */}
      <div className="pb-6 flex-1 min-w-0">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 w-full text-left"
        >
          <span className="font-semibold text-foreground">{step.title}</span>
          <div className="flex gap-1 ml-auto flex-shrink-0">
            {step.role && (
              <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Users size={10} />
                {step.role}
              </span>
            )}
            {step.duration && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full flex items-center gap-1">
                <Clock size={10} />
                {step.duration}
              </span>
            )}
            {step.detail && (isOpen ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />)}
          </div>
        </button>
        {isOpen && step.detail && (
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.detail}</p>
        )}
      </div>
    </div>
  );
}

function ProcessFlow({ block }) {
  return (
    <BlockShell grounded={block.grounded} className="bg-card rounded-xl p-5 border border-border">
      {block.title && (
        <p className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <GitBranch size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div>
        {(block.steps || []).map((step) => (
          <ProcessStep key={step.order} step={step} />
        ))}
      </div>
      {block.branching && (
        <p className="text-xs text-muted-foreground mt-1 italic">Branching: {block.branching}</p>
      )}
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 5. checklist
// ---------------------------------------------------------------------------
function Checklist({ block }) {
  var items = block.items || [];
  var checked = React.useState(() => items.map(() => false));
  var checkedState = checked[0];
  var setChecked = checked[1];

  var completedCount = checkedState.filter(Boolean).length;

  function toggle(i) {
    var next = checkedState.slice();
    next[i] = !next[i];
    setChecked(next);
  }

  return (
    <BlockShell grounded={block.grounded} className="bg-card rounded-xl p-5 border border-border">
      <div className="flex items-center justify-between mb-4">
        {block.title && <p className="font-semibold text-foreground">{block.title}</p>}
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
          {completedCount}/{items.length} done
        </span>
      </div>
      <div className="space-y-2">
        {items.map(function (item, i) {
          return (
            <div
              key={i}
              className="flex items-start gap-3 cursor-pointer group"
              onClick={() => toggle(i)}
            >
              <div
                className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  checkedState[i]
                    ? 'bg-primary border-primary'
                    : 'border-border group-hover:border-primary'
                }`}
              >
                {checkedState[i] && <Check size={12} className="text-primary-foreground" />}
              </div>
              <span
                className={`text-sm flex-1 leading-relaxed ${
                  checkedState[i] ? 'line-through text-muted-foreground' : 'text-foreground'
                }`}
              >
                {item.text}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                {item.mandatory && (
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-1" title="Mandatory" />
                )}
                {item.role && (
                  <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                    {item.role}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 6. parameter_table
// ---------------------------------------------------------------------------
function ParameterTable({ block }) {
  var vendors = block.vendor_columns || [];
  return (
    <BlockShell grounded={block.grounded}>
      {block.title && (
        <p className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <Layers size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted border-b border-border">
              <th className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap">Parameter</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap">Description</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap">Typical Value</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap">Unit</th>
              {vendors.map((v) => (
                <th key={v} className="text-left px-4 py-3 font-semibold text-primary whitespace-nowrap">{v}</th>
              ))}
              <th className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(block.rows || []).map((row, i) => (
              <tr key={i} className={`border-b border-border ${i % 2 === 1 ? 'bg-muted/40' : ''}`}>
                <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{row.parameter}</td>
                <td className="px-4 py-3 text-muted-foreground">{row.description}</td>
                <td className="px-4 py-3 text-foreground">{row.typical_value}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{row.unit}</td>
                {vendors.map((v) => (
                  <td key={v} className="px-4 py-3">
                    <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded font-medium">
                      {row.vendor_values && row.vendor_values[v] !== undefined ? row.vendor_values[v] : '—'}
                    </span>
                  </td>
                ))}
                <td className="px-4 py-3 text-muted-foreground text-xs">{row.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 7. kpi_matrix
// ---------------------------------------------------------------------------
function KpiMatrix({ block }) {
  var kpis = block.kpis || [];
  var scenarios = block.scenarios || [];
  var cells = block.cells || [];

  function findCell(kpi, scenario) {
    return cells.find((c) => c.kpi === kpi && c.scenario === scenario);
  }

  return (
    <BlockShell grounded={block.grounded}>
      {block.title && (
        <p className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <Target size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div className="overflow-x-auto">
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `minmax(120px, auto) repeat(${scenarios.length}, minmax(140px, 1fr))` }}
        >
          {/* header row */}
          <div className="text-xs font-semibold text-muted-foreground px-2 py-2">KPI \ Scenario</div>
          {scenarios.map((s) => (
            <div key={s} className="text-xs font-semibold text-primary px-3 py-2 bg-primary/10 rounded-lg text-center">{s}</div>
          ))}
          {/* data rows */}
          {kpis.map((kpi) => (
            <React.Fragment key={kpi}>
              <div className="text-sm font-medium text-foreground flex items-center px-2">{kpi}</div>
              {scenarios.map((scenario) => {
                var cell = findCell(kpi, scenario);
                return (
                  <div key={scenario} className="bg-card border border-border rounded-lg p-3">
                    {cell ? (
                      <>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                          <Target size={10} />
                          Target
                        </div>
                        <div className="font-semibold text-foreground text-sm">{cell.target}</div>
                        {cell.threshold && (
                          <div className="text-xs text-amber-400 mt-1">Threshold: {cell.threshold}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted-foreground text-xs">—</div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 8. topology_diagram
// ---------------------------------------------------------------------------
function TopologyDiagram({ block }) {
  var nodes = block.nodes || [];
  var edges = block.edges || [];

  // group nodes by kind
  var byKind = {};
  nodes.forEach(function (n) {
    var k = n.kind || 'Unknown';
    if (!byKind[k]) byKind[k] = [];
    byKind[k].push(n);
  });

  var nodeMap = {};
  nodes.forEach(function (n) { nodeMap[n.id] = n; });

  return (
    <BlockShell grounded={block.grounded} className="bg-card rounded-xl p-5 border border-border">
      {block.title && (
        <p className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Layers size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      {/* nodes by kind */}
      <div className="space-y-4 mb-5">
        {Object.keys(byKind).map(function (kind) {
          return (
            <div key={kind}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{kind}</p>
              <div className="flex flex-wrap gap-2">
                {byKind[kind].map(function (node) {
                  return (
                    <div key={node.id} className="bg-muted rounded-lg px-3 py-2 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                      <span className="text-sm font-medium text-foreground">{node.label}</span>
                      {node.vendor && (
                        <span className="text-xs text-muted-foreground">({node.vendor})</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* edges */}
      {edges.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Connections</p>
          <div className="space-y-1.5">
            {edges.map(function (edge, i) {
              var srcNode = nodeMap[edge.source_id];
              var tgtNode = nodeMap[edge.target_id];
              return (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="text-foreground font-medium">{srcNode ? srcNode.label : edge.source_id}</span>
                  <ArrowRight size={14} className="text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">{tgtNode ? tgtNode.label : edge.target_id}</span>
                  {edge.label && <span className="text-muted-foreground text-xs">({edge.label})</span>}
                  {edge.protocol && (
                    <span className="bg-blue-500/10 text-blue-400 text-xs px-1.5 py-0.5 rounded">{edge.protocol}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {block.layout_hint && (
        <p className="mt-3 text-xs text-muted-foreground italic">Layout: {block.layout_hint}</p>
      )}
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 9. workflow
// ---------------------------------------------------------------------------
function WorkflowStep({ step, allSteps, depth }) {
  depth = depth || 0;
  var expanded = React.useState(true);
  var isOpen = expanded[0];
  var setIsOpen = expanded[1];

  return (
    <div className={`${depth > 0 ? 'ml-6 border-l border-border pl-4' : ''}`}>
      <div className="bg-card border border-border rounded-lg p-3 mb-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 w-full text-left"
        >
          {step.decisions && step.decisions.length > 0
            ? (isOpen ? <ChevronDown size={14} className="text-primary flex-shrink-0" /> : <ChevronRight size={14} className="text-primary flex-shrink-0" />)
            : <div className="w-3.5 h-3.5 flex-shrink-0" />
          }
          <span className="font-medium text-foreground text-sm">{step.title}</span>
        </button>
        {isOpen && step.detail && (
          <p className="text-xs text-muted-foreground mt-2 ml-5 leading-relaxed">{step.detail}</p>
        )}
        {isOpen && step.decisions && step.decisions.length > 0 && (
          <div className="mt-3 ml-5 space-y-1">
            {step.decisions.map(function (dec, di) {
              var nextStep = allSteps.find((s) => s.id === dec.next_step_id);
              return (
                <div key={di} className="flex items-start gap-2 text-xs">
                  <span className="text-amber-400 flex-shrink-0">▸</span>
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{dec.condition}</span>
                    {nextStep && <span className="text-muted-foreground"> → {nextStep.title}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Workflow({ block }) {
  var steps = block.steps || [];
  return (
    <BlockShell grounded={block.grounded}>
      {block.title && (
        <p className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <GitBranch size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div>
        {steps.map(function (step) {
          return <WorkflowStep key={step.id} step={step} allSteps={steps} depth={0} />;
        })}
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 10. warning
// ---------------------------------------------------------------------------
function Warning({ block }) {
  var severity = block.severity || 'medium';
  var config = {
    low: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', Icon: Info },
    medium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', Icon: AlertCircle },
    high: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', Icon: AlertCircle },
    critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', Icon: ShieldAlert },
  }[severity] || { bg: 'bg-muted', border: 'border-border', text: 'text-foreground', Icon: AlertCircle };

  var Icon = config.Icon;

  return (
    <BlockShell grounded={block.grounded}>
      <div className={`rounded-xl border p-4 ${config.bg} ${config.border}`}>
        <div className="flex items-start gap-3">
          <Icon size={20} className={`${config.text} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className={`font-semibold text-sm ${config.text}`}>{block.headline}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${config.border} ${config.text} uppercase tracking-wider`}>{severity}</span>
            </div>
            {block.remediation && (
              <p className="text-sm text-foreground leading-relaxed">
                <span className="font-medium">Remediation:</span> {block.remediation}
              </p>
            )}
          </div>
        </div>
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 11. escalation
// ---------------------------------------------------------------------------
function Escalation({ block }) {
  return (
    <BlockShell grounded={block.grounded} className="bg-card rounded-xl p-5 border border-border">
      {block.title && (
        <p className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Users size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {(block.tiers || []).map(function (tier, i) {
          return (
            <div key={i} className="flex-1 min-w-[160px] bg-muted rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {tier.tier}
                </div>
                <span className="font-semibold text-foreground text-sm">{tier.role}</span>
              </div>
              {tier.contact && (
                <p className="text-xs text-muted-foreground mb-2">{tier.contact}</p>
              )}
              {tier.sla && (
                <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full mb-2 font-medium">
                  SLA: {tier.sla}
                </span>
              )}
              {tier.responsibilities && (
                <p className="text-xs text-muted-foreground leading-relaxed">{tier.responsibilities}</p>
              )}
            </div>
          );
        })}
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 12. troubleshooting_path
// ---------------------------------------------------------------------------
function TroubleNode({ node, nodeMap, depth }) {
  depth = depth || 0;
  var expanded = React.useState(depth === 0);
  var isOpen = expanded[0];
  var setIsOpen = expanded[1];

  var children = (node.children || []).map((id) => nodeMap[id]).filter(Boolean);

  return (
    <div className={`${depth > 0 ? 'ml-5 border-l border-border pl-3 mt-2' : ''}`}>
      <div
        className={`rounded-lg border border-border p-3 cursor-pointer bg-card hover:border-primary/40 transition-colors`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-start gap-2">
          {children.length > 0 ? (
            isOpen ? <ChevronDown size={14} className="text-primary mt-0.5 flex-shrink-0" /> : <ChevronRight size={14} className="text-primary mt-0.5 flex-shrink-0" />
          ) : (
            <div className="w-3.5 h-3.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground text-sm">{node.symptom_or_cause}</p>
            {node.action && (
              <p className={`text-xs text-muted-foreground mt-1 leading-relaxed ${isOpen ? 'block' : 'hidden'}`}>
                {node.action}
              </p>
            )}
          </div>
        </div>
      </div>
      {isOpen && children.map(function (child) {
        return <TroubleNode key={child.id} node={child} nodeMap={nodeMap} depth={depth + 1} />;
      })}
    </div>
  );
}

function TroubleshootingPath({ block }) {
  var nodes = block.nodes || [];
  var nodeMap = {};
  nodes.forEach(function (n) { nodeMap[n.id] = n; });
  var root = nodeMap[block.root_id] || nodes[0];

  return (
    <BlockShell grounded={block.grounded} className="bg-card rounded-xl p-5 border border-border">
      {block.title && (
        <p className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Zap size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      {root && <TroubleNode node={root} nodeMap={nodeMap} depth={0} />}
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 13. timeline
// ---------------------------------------------------------------------------
function Timeline({ block }) {
  var events = block.events || [];
  return (
    <BlockShell grounded={block.grounded}>
      {block.title && (
        <p className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Clock size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div className="relative">
        {/* vertical line */}
        <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
        <div className="space-y-6">
          {events.map(function (event, i) {
            return (
              <div key={i} className="flex gap-5 relative">
                {/* dot */}
                <div className="w-7 h-7 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center flex-shrink-0 z-10">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                </div>
                <div className="flex-1 pb-1">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">{event.when}</span>
                  <p className="font-semibold text-foreground text-sm mt-0.5">{event.title}</p>
                  {event.detail && (
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{event.detail}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 14. comparison
// ---------------------------------------------------------------------------
function Comparison({ block }) {
  var columns = block.columns || [];
  var rows = block.rows || [];
  return (
    <BlockShell grounded={block.grounded}>
      {block.title && (
        <p className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-primary" />
          {block.title}
        </p>
      )}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted border-b border-border">
              <th className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap">Aspect</th>
              {columns.map(function (col) {
                return (
                  <th key={col.label} className="text-left px-4 py-3 font-semibold text-primary whitespace-nowrap">
                    {col.label}
                    {col.summary && (
                      <div className="text-xs text-muted-foreground font-normal mt-0.5">{col.summary}</div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(function (row, i) {
              return (
                <tr key={i} className={`border-b border-border ${i % 2 === 1 ? 'bg-muted/40' : ''}`}>
                  <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{row.aspect}</td>
                  {columns.map(function (col) {
                    var val = row.values && row.values[col.label];
                    return (
                      <td key={col.label} className="px-4 py-3 text-muted-foreground">
                        {val !== undefined ? val : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// 15. executive_insight
// ---------------------------------------------------------------------------
function ExecutiveInsight({ block }) {
  return (
    <BlockShell grounded={block.grounded}>
      <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <TrendingUp size={22} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xl font-bold text-foreground leading-snug">{block.headline}</p>
        </div>
        {block.bullets && block.bullets.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {block.bullets.map(function (bullet, i) {
              return (
                <span
                  key={i}
                  className="bg-card border border-border rounded-full px-4 py-1.5 text-sm text-foreground"
                >
                  {bullet}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </BlockShell>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------
function renderBlock(block) {
  switch (block.type) {
    case 'section_heading':      return <SectionHeading key={block.id} block={block} />;
    case 'narrative':            return <Narrative key={block.id} block={block} />;
    case 'key_insight':          return <KeyInsight key={block.id} block={block} />;
    case 'process_flow':         return <ProcessFlow key={block.id} block={block} />;
    case 'checklist':            return <Checklist key={block.id} block={block} />;
    case 'parameter_table':      return <ParameterTable key={block.id} block={block} />;
    case 'kpi_matrix':           return <KpiMatrix key={block.id} block={block} />;
    case 'topology_diagram':     return <TopologyDiagram key={block.id} block={block} />;
    case 'workflow':             return <Workflow key={block.id} block={block} />;
    case 'warning':              return <Warning key={block.id} block={block} />;
    case 'escalation':           return <Escalation key={block.id} block={block} />;
    case 'troubleshooting_path': return <TroubleshootingPath key={block.id} block={block} />;
    case 'timeline':             return <Timeline key={block.id} block={block} />;
    case 'comparison':           return <Comparison key={block.id} block={block} />;
    case 'executive_insight':    return <ExecutiveInsight key={block.id} block={block} />;
    default:
      return (
        <div key={block.id} className="bg-muted rounded-lg p-3 text-xs text-muted-foreground">
          Unknown block type: <span className="font-mono font-medium">{block.type}</span>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Root export
// ---------------------------------------------------------------------------
export default function CsjBlockRenderer({ blocks }) {
  if (!blocks || blocks.length === 0) {
    return (
      <div className="text-muted-foreground text-sm italic py-8 text-center">
        No content blocks to display.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {blocks.map(renderBlock)}
    </div>
  );
}
